import { Dialog, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import { useMatch, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import IconCardModule from "../../molecules/IconCardModule";
import EmailIcon from "@mui/icons-material/Email";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FolderIcon from "@mui/icons-material/Folder";
import PrintIcon from "@mui/icons-material/Print";
import SecurityIcon from "@mui/icons-material/Security";
import PlaceIcon from "@mui/icons-material/Place";
import DisplaySettingsIcon from "@mui/icons-material/DisplaySettings";
import WebhookIcon from "@mui/icons-material/Webhook";
import TicketPrintTemplatePage from "./TicketPrintTemplate";
import PrintTemplateSettingsPage from "./PrintTemplateSettings";
import useBreakpoint from "../../../hooks/useBreakpoint";
import CitiesSettingsPage from "./CitiesSettings";
import ContrastIcon from "@mui/icons-material/Contrast";
import BrandingSettingsPage from "./BrandingSettings";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import GradingIcon from "@mui/icons-material/Grading";

export default function SettingsPage() {
  const { t } = useTranslation("Settings");
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const printTemplateMatch = useMatch("/m/settings/ticket-print-template/*");
  const printTemplateListDialog = printTemplateMatch?.params["*"] === "list";
  const printTemplateDialog =
    printTemplateMatch?.params["*"] !== "list" &&
    (printTemplateMatch?.params["*"] || "").length > 0;
  const settingsMatch = useMatch("/m/settings/*");
  const citiesDialog = settingsMatch?.params["*"] === "cities";
  const brandingDialog = settingsMatch?.params["*"] === "branding";

  const handleNavigate = (value: string) => {
    navigate(`/m/settings/${value}`);
  };

  const handlePrintTemplateClick = () => {
    navigate("/m/settings/ticket-print-template/list");
  };

  const handleCitiesDialogClick = () => {
    navigate("/m/settings/cities");
  };

  const handleBrandingDialogClick = () => {
    navigate("/m/settings/branding");
  };

  const handlePrintTemplateClose = () => {
    navigate("/m/settings");
  };
  const handleCitiesClose = () => {
    navigate("/m/settings");
  };

  const handleBrandingClose = () => {
    navigate("/m/settings");
  };

  return (
    <Paper sx={{ height: 1, bgcolor: "background.default", overflow: "auto" }}>
      <Stack sx={{ p: 2 }} spacing={1}>
        <Dialog
          open={printTemplateDialog}
          fullScreen={!breakpoint.md}
          PaperProps={{
            sx: { minWidth: { xs: undefined, md: 800 }, height: 1 },
          }}
        >
          <TicketPrintTemplatePage />
        </Dialog>
        <Dialog
          open={printTemplateListDialog}
          onClose={handlePrintTemplateClose}
          fullScreen={!breakpoint.md}
          PaperProps={{
            sx: {
              minWidth: { xs: undefined, md: 600 },
              height: { xs: undefined, md: 450 },
            },
          }}
        >
          <PrintTemplateSettingsPage />
        </Dialog>
        <Dialog
          open={citiesDialog}
          onClose={handleCitiesClose}
          fullScreen={!breakpoint.md}
          PaperProps={{
            sx: {
              minWidth: { xs: undefined, md: 500 },
              height: { xs: undefined, md: 450 },
            },
          }}
        >
          <CitiesSettingsPage />
        </Dialog>
        <Dialog
          open={brandingDialog}
          onClose={handleBrandingClose}
          fullScreen={!breakpoint.md}
          PaperProps={{
            sx: {
              sx: { minWidth: { xs: undefined, md: 800 } },
            },
          }}
        >
          <BrandingSettingsPage />
        </Dialog>

        <Typography color="primary" variant="h5" fontWeight={600}>
          {t("Settings")}
        </Typography>

        <Grid container rowSpacing={0.5}>
          <Grid size={{ xs: 12 }}>
            <Typography fontWeight={600} variant="h6" gutterBottom>
              {t("General")}
            </Typography>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("PrintTemplate.Title")}
              body={t("PrintTemplate.Body")}
              icon={PrintIcon}
              onClick={handlePrintTemplateClick}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("Cities.Title")}
              body={t("Cities.Body")}
              icon={PlaceIcon}
              onClick={handleCitiesDialogClick}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("Branding.Title")}
              body={t("Branding.Body")}
              icon={ContrastIcon}
              onClick={handleBrandingDialogClick}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("Security.Title")}
              body={t("Security.Body")}
              icon={SecurityIcon}
              onClick={() => handleNavigate("security")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("Logs.Title")}
              body={t("Logs.Body")}
              icon={GradingIcon}
              onClick={() => handleNavigate("logs")}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography fontWeight={600} variant="h6" gutterBottom>
              {t("EmailSetup")}
            </Typography>
            <Divider />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("MailMessageConfiguration.Title")}
              body={t("MailMessageConfiguration.Body")}
              icon={EmailOutlinedIcon}
              onClick={() => handleNavigate("messages")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("EmailForwarding.Title")}
              body={t("EmailForwarding.Body")}
              icon={EmailIcon}
              onClick={() => handleNavigate("forwards")}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography fontWeight={600} variant="h6" gutterBottom>
              {t("Integrations")}
            </Typography>
            <Divider />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("SMTP.Title")}
              body={t("SMTP.Body")}
              icon={DisplaySettingsIcon}
              onClick={() => handleNavigate("smtp")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("OpenAI.Title")}
              body={t("OpenAI.Body")}
              icon={AutoAwesomeIcon}
              onClick={() => handleNavigate("openai")}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <IconCardModule
              title={t("FTP.Title")}
              body={t("FTP.Body")}
              icon={FolderIcon}
              onClick={() => handleNavigate("ftp")}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, xl: 4 }}>
            <IconCardModule
              title={t("WebhooksAPI.Title")}
              body={t("WebhooksAPI.Body")}
              icon={WebhookIcon}
              onClick={() => handleNavigate("integrations")}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, xl: 4 }}>
            <IconCardModule
              title={t("Import.Title")}
              body={t("Import.Body")}
              icon={ImportExportIcon}
              onClick={() => handleNavigate("import")}
            />
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
}
